import React from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
} from '@material-ui/core';
import { Form, Formik, FormikHelpers } from 'formik';
import { useIntl } from 'react-intl';

import {
  OrganizationExtraDetailsState,
  defaultExtraDetailsState,
  validateExtraDetails,
} from 'app/configuration/organizations/organization-extra-details';
import { OrganizationExtraFields } from 'app/configuration/organizations/organization-extra-fields';
import { useStyles } from 'app/operations/drivers/driver-form';

interface Props {
  isOpen: boolean;
  organizationName: string;
  onSave: (
    values: OrganizationExtraDetailsState,
    helpers: FormikHelpers<OrganizationExtraDetailsState>
  ) => void;
  onSkip: () => void;
}

// Shown right after the Ruby "add organization" call succeeded: collects the
// columns the Ruby backend does not know about and saves them through Express.
const OrganizationExtraDetailsModal: React.FC<Props> = ({
  isOpen,
  organizationName,
  onSave,
  onSkip,
}) => {
  const classes = useStyles();
  const intl = useIntl();

  return (
    <Dialog
      open={isOpen}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      // no accidental dismissal: the user either saves or explicitly skips
      disableBackdropClick
      disableEscapeKeyDown
    >
      <Formik
        initialValues={defaultExtraDetailsState}
        validate={values => validateExtraDetails(values, intl)}
        onSubmit={onSave}
      >
        {({ isSubmitting }) => (
          <Form>
            <DialogTitle>
              {intl.formatMessage({ id: 'orgAdditionalDetails' })}

              {organizationName ? ` — ${organizationName}` : ''}
            </DialogTitle>

            <DialogContent dividers>
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ marginBottom: 16 }}
              >
                {intl.formatMessage({ id: 'orgAdditionalDetailsIntro' })}
              </Typography>

              <OrganizationExtraFields disabled={isSubmitting} />
            </DialogContent>

            {isSubmitting && <LinearProgress />}

            <DialogActions style={{ padding: '12px 24px' }}>
              <Button
                variant="outlined"
                color="primary"
                type="submit"
                disabled={isSubmitting}
                className={`${classes.submitButton} ${classes.actionButton}`}
              >
                {intl.formatMessage({ id: 'save' })}
              </Button>

              <Button
                variant="outlined"
                color="default"
                type="button"
                disabled={isSubmitting}
                onClick={onSkip}
                className={classes.actionButton}
              >
                {intl.formatMessage({ id: 'orgSkipForNow' })}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export { OrganizationExtraDetailsModal };
